import type { MaybeRefOrGetter } from '@vueuse/core'
import { downloadZip } from 'client-zip'
import type { ExposedDocument } from '@/components/document/DocumentArea.vue'

export function useDocument(id: MaybeRefOrGetter<string>) {
  const { isDirectSign } = storeToRefs(useDirectSignStore())
  const { documentRepository, directDocumentRepository } = useApi()

  const key = computed(() => `document:${toValue(id)}`)

  return useAsyncData(
    key,
    async () => {
      const documentId = toValue(id)

      return isDirectSign.value
        ? directDocumentRepository.getDocument(documentId)
        : documentRepository.getDocument(documentId)
    },
    {
      lazy: true,
      dedupe: 'defer',
    }
  )
}

export function useDocumentHelpers() {
  const directSignStore = useDirectSignStore()
  const visualSignatureStore = useVisualSignatureStore()
  const { directSignB64AuthHeader, isDirectSign } = storeToRefs(directSignStore)

  const { documentRepository, directDocumentRepository } = useApi()

  const { http } = useRuntimeConfig().public

  const getPagePreviewUrl = (documentId: number | string, page: number, scale: number): string => {
    const params = new URLSearchParams()
    params.append('scale', scale.toString())

    if (isDirectSign.value) {
      params.append('token', directSignB64AuthHeader.value)
      return `${http.browserBaseURL}/v3/direct/documents/${documentId}/pages/${page}?${params.toString()}`
    } else {
      return `${http.browserBaseURL}/v3/documents/${documentId}/pages/${page}?${params.toString()}`
    }
  }

  const getPagePreview = async (documentId: string, pageNumber: number, scale = 20, signal?: AbortSignal) => {
    if (directSignStore.isDirectSign) {
      const token = directSignStore.directSignB64AuthHeader
      return await directDocumentRepository.getDocumentPage(documentId, pageNumber, { scale, token }, signal)
    } else {
      return await documentRepository.getDocumentPage(documentId, pageNumber, scale, signal)
    }
  }

  const getItemPosition = (signature: PositionedSignature, pageNumber: number): SignaturePosition => ({
    page: String(pageNumber - 1), // Backend zero-indexes pages and expects a string
    x: signature.x,
    y: signature.y,
    width: signature.width,
    height: signature.height,
  })

  /**
   * Maps the visual signatures on a document to the corresponding signature placements our backend expects.
   *
   * @param placementMap The placement map to use as a reference.
   */
  const mapVisualSignatures = (placementMap: PlacementMap): SignaturePosition[] => {
    return [...placementMap.entries()].flatMap(([pageNumber, signatures]) => {
      return [...signatures.values()].map(signature => getItemPosition(signature, pageNumber))
    })
  }

  const mapSeals = (placementMap: PlacementMap): SignaturePosition[] => {
    return [...placementMap.entries()].flatMap(([pageNumber, seals]) => {
      return [...seals.values()].map(seal => {
        const { width, height } = visualSignatureStore.fitSealDimensions(seal.sealId ?? '', {
          width: seal.width,
          height: seal.height,
        })

        return getItemPosition({ ...seal, width, height }, pageNumber)
      })
    })
  }

  const mapDocumentSignatures = (
    documents: ExposedDocument[],
    signatures: DocumentSignature[],
    visualSignatureId?: string
  ) => {
    return documents
      .filter(doc => signatures.some(signature => signature.documentId === doc.id))
      .map(doc => {
        const positions = mapVisualSignatures(doc.placedSignatures)

        return Object.assign(
          { documentId: doc.id },
          visualSignatureId || positions.length
            ? {
                visualSignature: {
                  id: visualSignatureId,
                  ...(positions.length ? { positions } : {}),
                },
              }
            : {}
        )
      })
  }

  const mapDocumentSeals = (documents: ExposedDocument[], signatures: DocumentSignature[], sealId: string) => {
    return documents.reduce<DocumentSignature[]>((resultingSignatures, doc) => {
      // We currently only assume one seal per document
      const matchingSignature = signatures.filter(signature => signature.documentId === doc.id)[0]

      if (!matchingSignature) return resultingSignatures

      const documentSignature: DocumentSignature = {
        documentId: doc.id,
      }

      if (matchingSignature.visualSignature?.positions) {
        const adjustedPositions = matchingSignature.visualSignature.positions.map(position => {
          const { width, height } = visualSignatureStore.fitSealDimensions(sealId, {
            width: position.width,
            height: position.height,
          })

          return {
            ...position,
            width,
            height,
          }
        })

        documentSignature.visualSignature = {
          positions: adjustedPositions,
        }
      } else {
        const positions = mapSeals(doc.placedSignatures)
        if (positions.length) {
          documentSignature.visualSignature = {
            positions,
          }
        }
      }

      resultingSignatures.push(documentSignature)
      return resultingSignatures
    }, [])
  }

  return {
    getPagePreviewUrl,
    getPagePreview,
    getItemPosition,
    mapVisualSignatures,
    mapSeals,
    mapDocumentSignatures,
    mapDocumentSeals,
  }
}

export function useDocumentActions() {
  const { isDirectSign, directSignB64AuthHeader } = storeToRefs(useDirectSignStore())
  const { email } = storeToRefs(useUserStore())

  const {
    documentRepository: docsRepo,
    directDocumentRepository: directDocsRepo,
    srRepository: srRepo,
    directSrRepository: directSrRepo,
  } = useApi()

  const downloadDocuments = async (id: string, documentIds: string[], isSigned?: boolean) => {
    if (documentIds.length === 1) {
      const { data, name } = isDirectSign.value
        ? await directDocsRepo.getDocumentData(documentIds[0], directSignB64AuthHeader.value, isSigned)
        : await docsRepo.getDocumentData(documentIds[0], isSigned)
      downloadFile(data, name)
    } else {
      const { data, name } = isDirectSign.value
        ? await directSrRepo.getDocumentsZip(id, directSignB64AuthHeader.value, isSigned)
        : await srRepo.getDocumentsZip(id, isSigned)
      downloadFile(data, name)
    }
  }

  const batchDownloadDocuments = async (
    items: { id: string; title: string; documentIds: string[]; isSigned: boolean }[]
  ) => {
    const srResults = await Promise.all(
      items.map(async item => {
        const docs = await Promise.all(
          item.documentIds.map(docId =>
            isDirectSign.value
              ? directDocsRepo.getDocumentData(docId, directSignB64AuthHeader.value, item.isSigned)
              : docsRepo.getDocumentData(docId, item.isSigned)
          )
        )
        return { title: item.title, docs }
      })
    )

    const folderCounts = new Map<string, number>()
    const zipEntries: { name: string; input: Blob }[] = []

    for (const { title, docs } of srResults) {
      const count = folderCounts.get(title) ?? 0
      folderCounts.set(title, count + 1)
      const folder = count > 0 ? `${title} (${count})` : title

      for (const { data, name } of docs) {
        zipEntries.push({ name: `${folder}/${name}`, input: data })
      }
    }

    const zipBlob = await downloadZip(zipEntries).blob()
    const timestamp = Math.floor(Date.now() / 1000)
    downloadFile(zipBlob, `skribble-documents-${timestamp}.zip`)
  }

  const downloadProtocol = async (id: string, type: SignatureProtocolType = 'pdf') => {
    const { data, name } = await docsRepo.getProtocol(id, type)
    downloadFile(data, name)
  }

  const downloadAttachment = async (id: string, attachmentId: string, name?: string) => {
    const { data, name: attachmentName } = isDirectSign.value
      ? await directDocsRepo.getAttachment(id, attachmentId, directSignB64AuthHeader.value)
      : await docsRepo.getAttachment(id, attachmentId)

    downloadFile(data, name ?? attachmentName)
  }

  const assign = async (
    srData: SignatureRequestData,
    recipient: string,
    message: string
  ): Promise<DocumentActionResult> => {
    const signatureId = srData.signatures.find(
      sig => sig.accountEmail === email.value && sig.statusCode === 'OPEN'
    )?.sid

    if (!signatureId) throw new Error('No signature of assigning user found')

    const { status } = await srRepo.delegate(srData.id, {
      sid: signatureId,
      to: [recipient],
      message,
    })

    return status
  }

  const decline = async (id: string, message?: string): Promise<DocumentActionResult> => {
    const { status } = isDirectSign.value ? await directSrRepo.decline(id, message) : await srRepo.decline(id, message)

    return status
  }

  const withdraw = async (id: string, message?: string): Promise<DocumentActionResult> => {
    const { status } = await srRepo.withdraw(id, message)

    return status
  }

  const remove = async (id: string): Promise<DocumentActionResult> => {
    const { status } = await srRepo.remove(id)

    return status
  }

  const batchRemove = async (ids: string[]) => {
    await Promise.all(ids.map(id => remove(id)))
  }

  const remind = async (id: string) => {
    await srRepo.remindSigners(id)
  }

  const inviteSigner = async (id: string, email: string, documentIds: string[]) => {
    await srRepo.inviteSigner(id, email, documentIds)
  }

  const removeSigner = async (id: string, signatureId: string) => {
    await srRepo.removeSigner(id, signatureId)
  }

  const addObserver = async (id: string, observerData: CreateObserverData): Promise<ObserverData> => {
    return await srRepo.addObserver(id, observerData)
  }
  const removeObserver = async (id: string, observerId: string) => {
    return await srRepo.removeObserver(id, observerId)
  }

  return {
    downloadDocuments,
    batchDownloadDocuments,
    downloadProtocol,
    downloadAttachment,
    assign,
    decline,
    withdraw,
    remove,
    batchRemove,
    remind,
    inviteSigner,
    removeSigner,
    addObserver,
    removeObserver,
  }
}
